// TTS 모듈 공통 서비스
// TTSTest.js의 로직을 재사용 가능한 모듈로 추출

import appScriptService from './appScriptService';
import googleTTSService from './googleTTSService';

class TTSModuleService {
  constructor() {
    this.downloadProgress = {};
    this.progressCallback = null;
  }

  // 진행률 콜백 설정
  setProgressCallback(callback) {
    this.progressCallback = callback;
  }

  // 진행률 업데이트
  updateProgress(key, progress) {
    this.downloadProgress[key] = progress;
    if (this.progressCallback) {
      this.progressCallback(key, progress);
    }
  }

  // 모듈 데이터 로드 (TTSTest.js에서 추출)
  async loadModuleData(moduleCode) {

    
    try {
      const data = await appScriptService.getTTSScripts(moduleCode);

      
      if (data && data.sections && data.sections.length > 0) {
        return {
          success: true,
          data: data,
          sections: data.sections,
          title: data.title
        };
      } else {
        return {
          success: false,
          error: '섹션 데이터를 찾을 수 없습니다.',
          data: null
        };
      }
    } catch (error) {
      console.error('❌ 모듈 데이터 로드 실패:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  // TTS_Scripts 시트에 데이터 저장
  async savePodcastScript(moduleCode, scriptData) {

    
    try {
      // 기존 데이터 삭제 (선택적)
      // await this.clearExistingData(moduleCode);
      
      // 새 데이터 저장 준비
      const saveData = {
        course_id: moduleCode,
        course_title: scriptData.title,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        sections: scriptData.sections.map((section, index) => ({
          section_order: index + 1,
          section_title: section.title,
          section_content: section.content
        }))
      };

      // Google Sheets에 저장 (구현은 appScriptService에 따라 조정)
      const result = await appScriptService.addData('TTS_Scripts', saveData);
      

      return { success: true, result };
      
    } catch (error) {
      console.error('❌ TTS_Scripts 저장 실패:', error);
      return { success: false, error: error.message };
    }
  }

  // 개별 섹션 TTS 생성 (TTSTest.js에서 추출 및 개선)
  async generateSectionTTS(sectionIndex, section, options = {}) {
    const sectionKey = `section_${sectionIndex}`;
    
    try {
      this.updateProgress(sectionKey, { 
        status: 'starting', 
        progress: 0,
        section: section.section_title || section.title 
      });
      
      // 텍스트를 청크로 분할 (section_content 또는 content 필드 지원)
      const textContent = section.section_content || section.content || '';
      
      if (!textContent.trim()) {

        this.updateProgress(sectionKey, { 
          status: 'error', 
          error: '텍스트 내용이 없습니다.',
          section: section.section_title || section.title 
        });
        return { success: false, error: '텍스트 내용이 없습니다.' };
      }
      
      const chunks = googleTTSService.splitTextIntoChunks(textContent, 4800);
      const audioUrls = [];
      

      
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const progress = Math.round(((chunkIndex + 1) / chunks.length) * 100);
        
        this.updateProgress(sectionKey, {
          status: 'generating',
          progress: progress,
          chunk: chunkIndex + 1,
          totalChunks: chunks.length,
          section: section.section_title || section.title
        });
        
        try {
          // 프록시 API 사용
          const response = await fetch('/api/tts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              text: chunks[chunkIndex],
              options: {
                languageCode: options.languageCode || 'ko-KR',
                voiceName: options.voiceName || 'ko-KR-Chirp3-HD-Algieba',
                gender: options.gender || 'FEMALE',
                speakingRate: options.speakingRate || 1.0,
                pitch: options.pitch || 0.0
              }
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP ${response.status}`);
          }
          
          const data = await response.json();
          const audioUrl = googleTTSService.createAudioUrl(data.audioContent);
          audioUrls.push(audioUrl);
          
          // API 제한 방지 딜레이
          if (chunkIndex < chunks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
        } catch (error) {
          console.error(`섹션 ${sectionIndex + 1}, 청크 ${chunkIndex + 1} 생성 실패:`, error);
          
          this.updateProgress(sectionKey, { 
            status: 'error', 
            error: error.message,
            section: section.section_title || section.title 
          });
          
          return { success: false, error: error.message };
        }
      }
      
      // 다운로드 처리
      const sectionTitle = section.section_title || section.title;
      this.updateProgress(sectionKey, { 
        status: 'downloading',
        section: sectionTitle 
      });
      
      if (audioUrls.length === 1) {
        googleTTSService.downloadAudio(audioUrls[0], `${sectionTitle}.mp3`);
      } else {
        await googleTTSService.downloadMultipleAudios(audioUrls, sectionTitle);
      }
      
      this.updateProgress(sectionKey, { 
        status: 'completed',
        section: sectionTitle 
      });
      
      // 완료 후 5초 뒤 상태 초기화
      setTimeout(() => {
        delete this.downloadProgress[sectionKey];
        if (this.progressCallback) {
          this.progressCallback(sectionKey, null);
        }
      }, 5000);
      
      return { 
        success: true, 
        audioUrls, 
        section: sectionTitle 
      };
      
    } catch (error) {
      console.error(`섹션 ${sectionIndex + 1} TTS 생성 실패:`, error);
      
      this.updateProgress(sectionKey, { 
        status: 'error', 
        error: error.message,
        section: section.section_title || section.title 
      });
      
      return { success: false, error: error.message };
    }
  }

  // 전체 섹션 배치 처리
  async generateAllSectionsTTS(sections, options = {}) {

    
    const results = [];
    
    for (let i = 0; i < sections.length; i++) {
      const result = await this.generateSectionTTS(i, sections[i], options);
      results.push(result);
      
      // 섹션 간 딜레이
      if (i < sections.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    const successCount = results.filter(r => r.success).length;

    
    return {
      success: successCount === sections.length,
      results,
      successCount,
      totalCount: sections.length
    };
  }

  // 현재 진행률 상태 조회
  getProgressStatus() {
    return { ...this.downloadProgress };
  }

  // 진행률 초기화
  clearProgress() {
    this.downloadProgress = {};
  }
}

// 싱글톤 인스턴스
const ttsModuleService = new TTSModuleService();

export default ttsModuleService;